<script lang="ts" setup>
import { ref } from "vue"
import { ButtonSize, TuButton, TuPopover, usePopover } from "@tinyurldev/tu-ui-vue"

import useTranslation from "@Composables/useTranslation"

//
// Setup
const { __ } = useTranslation()

//
// Props
export interface DropdownItem {
  id: string
  label: string
  onClick: () => void
}

interface Props {
  items: DropdownItem[]
  selected?: string
  ariaLabel?: string
}

withDefaults(defineProps<Props>(), {
  selected: "",
  ariaLabel: "",
})

//
// Data
const isPopoverVisible = ref(false)
const { referenceRef, popoverRef } = usePopover(isPopoverVisible, { placement: "bottom-end" })

//
// Methods
function onToggle(): void {
  isPopoverVisible.value = !isPopoverVisible.value
}

function onItemClick(item: DropdownItem): void {
  isPopoverVisible.value = false
  item.onClick()
}

//
// Exposed Test Vars
defineExpose({ isPopoverVisible })
</script>

<template>
  <div class="org-dropdown">
    <TuButton
      ref="referenceRef"
      class="_dropdown_trigger"
      :aria-label="ariaLabel || __('common.actions')"
      :label="selected"
      is-outlined
      :size="ButtonSize.LARGE"
      :icon-class="isPopoverVisible ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
      @click="onToggle"
    />

    <TuPopover
      v-show="isPopoverVisible"
      ref="popoverRef"
    >
      <div class="org-dropdown__menu _dropdown_menu">
        <button
          v-for="item in items"
          :key="item.id"
          :class="['org-dropdown__menu-item', `_${item.id}`]"
          type="button"
          @click="onItemClick(item)"
        >
          {{ item.label }}
        </button>
      </div>
    </TuPopover>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.org-dropdown {
  &__menu {
    display: flex;
    flex-direction: column;
    min-width: 120px;
    padding: spacing.$xs-5 0;

    background-color: color.$background-secondary;
    border-radius: border.$medium-radius;
  }

  &__menu-item {
    display: flex;
    align-items: center;
    gap: spacing.$xxxxs;
    width: 100%;
    padding: spacing.$xxxxs spacing.$xxxs;

    font-size: typography.$xs-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$xs-line-height;
    text-align: left;
    color: color.$text;

    background: none;
    border: none;
    cursor: pointer;

    &:hover {
      background-color: color.$background-primary;
    }
  }
}
</style>
